/**
 *  All api related to shopping cart
 */
import _ from 'lodash'
import axios from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

// - checkout
export function checkout({
  cartId,
  checksum,
  params = {},
  payload = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'checkout',
    () => source.cancel('checkout: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `carts/${cartId}/checkout`,
      method: 'post',
      params,
      data: {
        data: {
          cart: {
            checksum
          }
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      ...data
    }))
}

// - create guest shopping cart
// - merge cart after register / login
export function createCart({
  params = {},
  payload = {},
  ...otherOptions
} = {}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'createCart',
    () => source.cancel('createCart: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'carts',
      method: 'post',
      params,
      data: {
        data: {
          cart: {}
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      ...data
    }))
}

// - create isolate cart
export function createIsolateCart({
  cartId,
  checksum,
  params = {},
  payload = {},
  ...otherOptions
}) {
  if (_.isEmpty((cartId || '').toString())) {
    console.error('createCartIsolate: missing parameter "cartId"')
    return Promise.reject()
  }
  if (_.isEmpty(checksum)) {
    console.error('createCartIsolate: missing parameter "checksum"')
    return Promise.reject()
  }

  const source = axios.CancelToken.source()
  cancelRequest.add(
    'createIsolateCart',
    () => source.cancel('createIsolateCart: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `carts/${cartId}/isolate`,
      method: 'post',
      params,
      data: {
        data: {
          cart: {
            checksum
          }
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      ...data
    }))
}

// - fetch shopping cart
// - fetch isolate cart
export function fetchCart({
  cartId,
  params = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  let cancelKey = 'fetchCart'
  if (params.cancelKey) {
    cancelKey = params.cancelKey
  }
  cancelRequest.add(
    cancelKey,
    () => source.cancel(`${cancelKey}: Operation cancelled by user`)
  )

  return OmnitechApi
    .request({
      endpoint: `carts/${cartId}`,
      method: 'get',
      params: _.omit(params, 'cancelKey'),
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(({ data, commerceChannel }) => {
      if (isErrorExist(data)) throw convertResponseToErrorObject(data, { useResponseMessage: true })
      return {
        ..._.get(data, 'data', {}),
        commerceChannel,
      }
    })
}

// - add item
// - update qty
// - remove item
// - apply delivery address / billing address
// - apply courier service
// - apply coupon
// - remove coupon
// - apply meta
export function updateCart({
  cartId,
  params,
  payload,
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'updateCart',
    () => source.cancel('updateCart: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `carts/${cartId}`,
      method: 'put',
      data: payload,
      params,
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      ...data
    }))
}
